import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, Statistic, Descriptions, Button, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { GET_BALANCE } from 'graphql/queries/balance';
import { BalanceQueryResponse } from 'graphql/types';
import { usePermission } from 'components/PermissionProvider';

const REFRESH_COOLDOWN_SECONDS = 5;

const formatAmount = (value: string, currency: string): string => {
    try {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 2,
        }).format(num);
    } catch {
        return value;
    }
};

const BalancePage: React.FC = () => {
    const { t } = useTranslation();
    const { hasPermission } = usePermission();
    const { data, loading, refetch } = useQuery<BalanceQueryResponse>(GET_BALANCE);
    const [cooldown, setCooldown] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const handleRefresh = useCallback(() => {
        if (cooldown > 0) return;
        refetch();
        setCooldown(REFRESH_COOLDOWN_SECONDS);
    }, [cooldown, refetch]);

    useEffect(() => {
        if (cooldown <= 0) {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }
        timerRef.current = setInterval(() => {
            setCooldown(prev => {
                if (prev <= 1) {
                    if (timerRef.current) clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [cooldown]);

    const balance = data?.portalBalance;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">{t('balance.title')}</h2>
                {hasPermission('portal:balance:refresh') && (
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        disabled={cooldown > 0}
                    >
                        {cooldown > 0 ? `${t('balance.refresh')} (${cooldown}s)` : t('balance.refresh')}
                    </Button>
                )}
            </div>

            <Spin spinning={loading}>
                <Card>
                    <Statistic
                        title={t('balance.balance')}
                        value={balance ? formatAmount(balance.balance, balance.currency) : '-'}
                        valueStyle={{ color: '#722ed1', fontSize: 32 }}
                    />
                    <Descriptions column={1} className="mt-6" size="small">
                        <Descriptions.Item label={t('balance.customerName')}>
                            {balance?.customerName || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('balance.currency')}>
                            {balance?.currency || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('balance.updatedAt')}>
                            {balance?.updatedAt ? dayjs(balance.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            </Spin>
        </div>
    );
};

export default BalancePage;
